import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Clock, ArrowLeft, Minus, Plus, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import EventWaitlistForm from '../components/EventWaitlistForm';
import FeeBreakdown from '../components/checkout/FeeBreakdown';
import TierDescription from '../components/checkout/TierDescription';
import SignupNudgeCard from '../components/SignupNudgeCard';
import Seo from '../components/Seo';
import EventJsonLd from '../components/EventJsonLd';
import { trackPurchase, trackInitiateCheckout, trackViewContent } from '../lib/metaPixel';
import { useCart } from '../context/CartContext';
import CartBar from '../components/cart/CartBar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // Server-computed fee breakdown — refreshed whenever the tier or quantity
  // changes. This is the SOLE source of truth for the total shown on screen
  // and the amount Razorpay charges.
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  // Discounts: `preapplied` is auto-applied by the server. `promoInput`/
  // `appliedPromo` are the buyer-typed code flow — validated server-side.
  const [preappliedDiscount, setPreappliedDiscount] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState('');
  const [promoError, setPromoError] = useState('');
  // Multi-day — the EventDate.id the buyer wants to attend. Required for
  // single-date tickets on a multi-day event; auto-picked when the tier is
  // scoped to exactly one date; ignored for cross-date passes.
  const [selectedDateId, setSelectedDateId] = useState(null);
  const cart = useCart();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/${id}`);
        setEvent(response.data);
        if (response.data.ticket_tiers?.length > 0) {
          setSelectedTier(response.data.ticket_tiers[0]);
        }
        // Meta ViewContent — powers "browsed this event but didn't buy"
        // retargeting audiences. Value is the cheapest tier so Meta has a
        // realistic conversion value estimate to bid against.
        const tiers = response.data.ticket_tiers || [];
        const cheapest = tiers.length
          ? Math.min(...tiers.map((t) => Number(t.price)).filter((n) => Number.isFinite(n)))
          : undefined;
        trackViewContent({
          contentIds: [response.data.id],
          contentName: response.data.title,
          contentCategory: response.data.genre,
          value: cheapest,
        });
      } catch (error) {
        console.error('Error fetching event:', error);
        toast.error('Event not found');
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
    
    // Preload Razorpay script
    loadRazorpayScript();

    // Public — see if the event has a pre-applied discount so we can
    // surface a "X% OFF" badge on the ticket card before checkout.
    axios
      .get(`${API}/events/${id}/discounts/preapplied`)
      .then((r) => setPreappliedDiscount(r.data || null))
      .catch(() => setPreappliedDiscount(null));
  }, [id, navigate]);

  // Derived flag — is the current wall-clock past the event's sales-close
  // time (HH:MM on the event date, IST)? When true, we grey out BUY.
  // If either field is missing, we treat as still-open.
  const salesPastCutoff = (() => {
    if (!event?.sales_close_time || !event?.date) return false;
    try {
      const deadline = new Date(`${event.date}T${event.sales_close_time}:00+05:30`);
      return !isNaN(deadline.getTime()) && Date.now() >= deadline.getTime();
    } catch {
      return false;
    }
  })();

  // Fetch server-authoritative quote whenever the buyer's picks change.
  // Public endpoint — works for guests too. Includes any applied promo code
  // or auto-applied event discount.
  useEffect(() => {
    if (!selectedTier || !event?.id) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    const params = { event_id: event.id, tier_id: selectedTier.id, quantity };
    if (appliedPromo) params.discount_code = appliedPromo;
    // Multi-day: pass the picked date so quote returns the right breakdown.
    // Server ignores for cross-date passes; required for single-date tiers.
    if (event.is_multi_day && selectedDateId) params.date_id = selectedDateId;
    axios
      .get(`${API}/checkout/quote`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((r) => {
        if (cancelled) return;
        setQuote(r.data);
        // Surface server-side discount errors (e.g. promo expired after we
        // last validated it client-side). Clears the applied promo so the
        // total matches reality.
        if (r.data?.discount_error && appliedPromo) {
          setPromoError(r.data.discount_error);
          setAppliedPromo('');
        }
      })
      .catch(() => {
        if (!cancelled) setQuote(null);
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [event?.id, selectedTier, quantity, token, appliedPromo, selectedDateId]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    }).toUpperCase();
  };

  // Guest-checkout state — collected via a modal for BOTH guest and signed-in
  // buyers so we always capture buyer_phone (User model doesn't carry one).
  const [showGuestGate, setShowGuestGate] = useState(false);
  const [guestBuyer, setGuestBuyer] = useState({ name: '', email: '', phone: '', agreed_terms: false });

  // Prefill name/email from the signed-in user's profile when we have one.
  useEffect(() => {
    if (user) {
      setGuestBuyer((prev) => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || '',
      }));
    }
  }, [user]);

  const handleBuyTickets = () => {
    if (!selectedTier) {
      toast.error('Pick a ticket tier first');
      return;
    }
    // Free tickets require signup — redirect unauthenticated users to login
    // instead of the guest-checkout flow (prevents abuse of ₹0 inventory).
    const isFree = Number(selectedTier?.price || 0) <= 0;
    if (isFree && !token) {
      toast.error('Please sign in or create an account to claim free tickets');
      navigate(`/login?redirect=/events/${id}`);
      return;
    }
    // ALWAYS open the checkout gate first (guest or signed-in) so we can
    // capture a phone number + T&C consent. Signed-in users get their
    // name/email pre-filled and only need to add phone + tick T&C.
    setShowGuestGate(true);
  };

  const handleRazorpayPayment = useCallback(async () => {
    if (!selectedTier) return;

    setProcessingCheckout(true);

    // Ensure Razorpay is loaded
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      toast.error('Failed to load payment gateway');
      setProcessingCheckout(false);
      return;
    }

    // Buyer contact — collected via the checkout gate for BOTH guest and
    // signed-in buyers. This is the only reliable way to get a phone number
    // since the User model doesn't carry one.
    const buyerContact = { name: guestBuyer.name, email: guestBuyer.email, phone: guestBuyer.phone };

    try {
      // When the cart has items for THIS event, use the combined cart
      // endpoint so one Razorpay order covers every tier. Falls back to the
      // single-tier endpoint for the legacy "pick a tier, click BUY" flow.
      const useCartEndpoint = cart.items.length > 0 && cart.eventId === id;
      const createEndpoint = useCartEndpoint ? '/checkout/create-cart-order' : '/checkout/create-order';
      const createPayload = useCartEndpoint
        ? {
            event_id: id,
            items: cart.items.map((it) => ({
              tier_id: it.tier_id,
              quantity: it.quantity,
              date_id: it.date_id || null,
            })),
            buyer_name: buyerContact.name,
            buyer_email: buyerContact.email,
            buyer_phone: buyerContact.phone,
            discount_code: appliedPromo || null,
          }
        : {
            event_id: id,
            tier_id: selectedTier.id,
            quantity,
            buyer_name: buyerContact.name,
            buyer_email: buyerContact.email,
            buyer_phone: buyerContact.phone,
            discount_code: appliedPromo || null,
            date_id: event.is_multi_day ? (selectedDateId || null) : null,
          };
      const orderResponse = await axios.post(
        `${API}${createEndpoint}`,
        createPayload,
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );

      const { order_id, amount, currency, key_id, event_title } = orderResponse.data;
      const tier_name = orderResponse.data.tier_name || (cart.items[0]?.tier_name || 'Multiple');

      // Free-ticket short-circuit — backend fulfilled synchronously; just
      // show the success screen and skip Razorpay entirely.
      if (orderResponse.data.free) {
        trackPurchase({
          value: 0,
          currency: currency || 'INR',
          contentIds: [id],
          contentName: `${event_title} · ${tier_name}`,
          numItems: quantity,
        });
        setPaymentSuccess(true);
        setProcessingCheckout(false);
        cart.clear();
        toast.success(`${quantity} free ticket${quantity > 1 ? 's' : ''} claimed — check your email!`);
        return;
      }

      // Fire Meta InitiateCheckout — the moment the buyer commits to pay,
      // right before Razorpay's modal opens. Amount is in paise → rupees.
      trackInitiateCheckout({
        value: amount / 100,
        currency,
        numItems: quantity,
        contentName: `${event_title} · ${tier_name}`,
      });

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "Unight",
        description: `${event_title} - ${tier_name} x ${quantity}`,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment on backend
            await axios.post(
              `${API}/checkout/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              token ? { headers: { Authorization: `Bearer ${token}` } } : {}
            );

            setPaymentSuccess(true);
            setProcessingCheckout(false);
            cart.clear();
            toast.success('Payment successful! Your tickets are confirmed.');
            // Fire Meta Purchase — after backend confirms the payment
            // signature, so we never send a Purchase for a failed / spoofed
            // Razorpay callback. Value in rupees (amount was in paise).
            trackPurchase({
              value: amount / 100,
              currency,
              contentIds: [selectedTier.id],
              contentName: `${event_title} · ${tier_name}`,
              numItems: quantity,
            });
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
            setProcessingCheckout(false);
          }
        },
        prefill: {
          name: buyerContact.name || "",
          email: buyerContact.email || "",
          contact: buyerContact.phone || "",
        },
        theme: {
          color: "#B5FF00"
        },
        modal: {
          ondismiss: () => {
            setProcessingCheckout(false);
          },
          escape: true,
          backdropclose: false
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        console.error('Payment failed:', response.error);
        toast.error(`Payment failed: ${response.error.description}`);
        setProcessingCheckout(false);
      });

      razorpay.open();

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
      setProcessingCheckout(false);
    }
  }, [selectedTier, id, quantity, token, user, guestBuyer, appliedPromo]);

  const handleApplyPromo = async () => {
    const code = (promoInput || '').trim().toUpperCase();
    if (!code) {
      setPromoError('Enter a promo code');
      return;
    }
    try {
      await axios.post(
        `${API}/events/${id}/discounts/validate`,
        { code, quantity, buyer_email: guestBuyer.email || user?.email || undefined },
      );
      setAppliedPromo(code);
      setPromoError('');
      toast.success('Promo applied');
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid promo code');
    }
  };

  const handleClearPromo = () => {
    setAppliedPromo('');
    setPromoInput('');
    setPromoError('');
  };

  // Site is public — no auth-gating on event pages.
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="loading">
        <div className="text-[#B5FF00] font-mono uppercase tracking-widest animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  if (!event) return null;

  const totalPrice = selectedTier ? selectedTier.price * quantity : 0;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="event-detail-page">
      <Seo
        title={event.title}
        description={
          event.description
            ? String(event.description).slice(0, 160)
            : `${event.title} at ${event.venue || 'a Unight venue'} on ${event.date}. Book tickets or join the guest list on Unight.`
        }
        path={`/events/${event.id}`}
        image={event.image_url || undefined}
      />
      <EventJsonLd event={event} />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <img 
            src={event.image_url} 
            alt={event.title}
            className="w-full h-full object-cover img-grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
        </div>

        <button 
          onClick={() => navigate('/events')}
          className="absolute top-24 md:top-28 left-4 md:left-8 z-20 flex items-center gap-2 text-zinc-400 hover:text-[#B5FF00] transition-colors font-mono text-sm tracking-widest"
          data-testid="back-button"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          BACK
        </button>

        <div className="relative container mx-auto px-4 md:px-8 pb-12 md:pb-16 pt-32">
          <div className="max-w-4xl">
            <span className="font-mono text-xs tracking-widest text-[#B5FF00] border border-[#B5FF00]/30 px-3 py-1 mb-4 inline-block animate-fade-in">
              {event.genre.toUpperCase()}
            </span>

            <h1 
              className="font-['Josefin_Sans'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-none mb-6 animate-fade-in-up"
              data-testid="event-title"
            >
              {event.title}
            </h1>

            <div className="flex flex-wrap gap-4 md:gap-8 animate-fade-in-up stagger-2" style={{ animationFillMode: 'forwards' }}>
              <div className="flex items-center gap-2 text-zinc-400">
                <Calendar size={18} strokeWidth={1.5} className="text-[#B5FF00]" />
                <span className="font-mono text-sm tracking-wider">
                  {formatDate(event.date)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Clock size={18} strokeWidth={1.5} className="text-[#B5FF00]" />
                <span className="font-mono text-sm tracking-wider">
                  {event.time}
                </span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <MapPin size={18} strokeWidth={1.5} className="text-[#B5FF00]" />
                <span className="font-mono text-sm tracking-wider">
                  {event.venue}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 border-t border-zinc-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-16">
            {/* Left Column - Details */}
            <div className="lg:col-span-7 space-y-12">
              <div>
                <h2 className="font-['Josefin_Sans'] text-2xl md:text-3xl font-bold text-white mb-4">
                  ABOUT
                </h2>
                <div className="space-y-4 text-zinc-300 text-base md:text-lg leading-loose" data-testid="event-description">
                  {String(event.description || '')
                    // Split on blank lines to build paragraphs; single newlines
                    // inside a paragraph become <br /> so the writer's line
                    // breaks are preserved. Loose leading (line-height:2) +
                    // paragraph gaps stop the block feeling cramped.
                    .split(/\n\s*\n/)
                    .filter(p => p.trim())
                    .map((paragraph, i) => (
                      <p key={i} className="whitespace-pre-line">
                        {paragraph.trim()}
                      </p>
                    ))}
                </div>
              </div>

              {event.lineup && event.lineup.length > 0 && (
                <div>
                  <h2 className="font-['Josefin_Sans'] text-2xl md:text-3xl font-bold text-white mb-6">
                    LINEUP
                  </h2>
                  <div className="space-y-4" data-testid="event-lineup">
                    {event.lineup.map((artist, index) => (
                      <div 
                        key={artist}
                        className="flex items-center gap-4 py-4 border-b border-zinc-800 opacity-0 animate-slide-in-left"
                        style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                      >
                        <span className="font-mono text-xs text-zinc-600 w-8">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="font-['Josefin_Sans'] text-xl md:text-2xl text-white hover:text-[#B5FF00] transition-colors cursor-default">
                          {artist}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h2 className="font-['Josefin_Sans'] text-2xl md:text-3xl font-bold text-white mb-4">
                  VENUE
                </h2>
                <div className="bg-[#0A0A0A] border border-zinc-800 p-6">
                  <h3 className="font-['Josefin_Sans'] text-xl text-white mb-2">{event.venue}</h3>
                  <p className="text-zinc-400 font-mono text-sm">{event.address}</p>
                </div>
              </div>
            </div>

            {/* Right Column - Tickets OR Waitlist form */}
            <div className="lg:col-span-5">
              <div className="sticky top-28">
                {event.waitlist_only ? (
                  <EventWaitlistForm event={event} />
                ) : (
                <div className="bg-[#0A0A0A] border border-zinc-800 p-6 md:p-8" data-testid="ticket-section">
                  <h2 className="font-['Josefin_Sans'] text-2xl font-bold text-white mb-6">
                    GET TICKETS
                  </h2>

                  {preappliedDiscount && (
                    <div
                      className="mb-4 p-3 border border-[#B5FF00]/40 bg-[#B5FF00]/5 flex items-center gap-3"
                      data-testid="preapplied-discount-badge"
                    >
                      <span className="font-mono text-2xl text-[#B5FF00] font-bold">
                        -{Math.round(preappliedDiscount.percent_off)}%
                      </span>
                      <div>
                        <p className="text-white text-sm font-['Josefin_Sans']">{preappliedDiscount.name}</p>
                        <p className="font-mono text-[11px] text-zinc-400 tracking-wider">
                          AUTO-APPLIED AT CHECKOUT
                          {preappliedDiscount.min_quantity ? ` · MIN ${preappliedDiscount.min_quantity} TICKETS` : ''}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Multi-day date selector — shown before tiers. For single-day events this is hidden. */}
                  {event.is_multi_day && Array.isArray(event.event_dates) && event.event_dates.length > 0 && (
                    <div className="mb-6" data-testid="date-selector">
                      <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                        SELECT DATE
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {event.event_dates.map((d) => {
                          const sel = selectedDateId === d.id;
                          return (
                            <button
                              key={d.id}
                              type="button"
                              onClick={() => { setSelectedDateId(d.id); setSelectedTier(null); }}
                              className={`p-3 border text-left transition-all ${
                                sel
                                  ? 'border-[#B5FF00] bg-[#B5FF00]/5'
                                  : 'border-zinc-800 hover:border-zinc-700'
                              }`}
                              data-testid={`date-btn-${d.id}`}
                            >
                              <div className="font-mono text-xs text-[#B5FF00] tracking-wider">{d.label || `DAY ${event.event_dates.indexOf(d) + 1}`}</div>
                              <div className="text-white text-sm font-['Josefin_Sans'] mt-0.5">{d.date}</div>
                              <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{d.time}</div>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-zinc-500 text-xs mt-2">
                        Pick which night you&apos;re attending — ticket options will filter to that date. Weekend / multi-day passes always include every date.
                      </p>
                    </div>
                  )}

                  <div className="space-y-3 mb-6">
                    {(() => {
                      // Multi-day filtering rule: when a date is selected, show tiers whose
                      // valid_date_ids is empty (all dates), OR contains the selected date
                      // (single-day tier for THAT date), OR has 2+ dates INCLUDING the selected
                      // one (weekend/multi-day pass eligible from this date). When no date
                      // is selected (either because the event is single-day or the buyer
                      // hasn't picked yet), show every tier.
                      const tiers = event.ticket_tiers || [];
                      if (!event.is_multi_day || !selectedDateId) return tiers;
                      return tiers.filter((t) => {
                        const ids = Array.isArray(t.valid_date_ids) ? t.valid_date_ids : null;
                        if (!ids || ids.length === 0) return true;
                        return ids.includes(selectedDateId);
                      });
                    })().map((tier) => {
                      const soldOut = tier.quantity_available === 0;
                      const ids = Array.isArray(tier.valid_date_ids) ? tier.valid_date_ids : null;
                      const isPass = ids && ids.length > 1;
                      return (
                        <div
                          key={tier.id}
                          onClick={() => !soldOut && setSelectedTier(tier)}
                          role="button"
                          tabIndex={soldOut ? -1 : 0}
                          onKeyDown={(e) => {
                            if (!soldOut && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              setSelectedTier(tier);
                            }
                          }}
                          className={`w-full p-4 border transition-all text-left ${
                            selectedTier?.id === tier.id
                              ? 'border-[#B5FF00] bg-[#B5FF00]/5'
                              : 'border-zinc-800 hover:border-zinc-700'
                          } ${soldOut ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid={`tier-${tier.id}`}
                          aria-disabled={soldOut}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex-1">
                              <span className="font-['Josefin_Sans'] text-lg text-white">{tier.name}</span>
                              {isPass && (
                                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-mono tracking-wider bg-[#B5FF00]/10 text-[#B5FF00] border border-[#B5FF00]/30 align-middle">
                                  {ids.length}-DAY PASS
                                </span>
                              )}
                            </div>
                            <span className="font-mono text-[#B5FF00]">₹{tier.price.toFixed(0)}</span>
                          </div>
                          <TierDescription text={tier.description} />
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-zinc-600 text-xs font-mono">
                              {soldOut
                                ? 'SOLD OUT'
                                : tier.hide_remaining
                                  ? 'AVAILABLE'
                                  : `${tier.quantity_available} remaining`}
                            </p>
                            {!soldOut && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const needsDate = event.is_multi_day && (!Array.isArray(tier.valid_date_ids) || tier.valid_date_ids.length === 0);
                                  if (needsDate && !selectedDateId) {
                                    toast.error('Pick a date first');
                                    return;
                                  }
                                  cart.addItem(event.id, {
                                    tier_id: tier.id,
                                    tier_name: tier.name,
                                    unit_price: Number(tier.price || 0),
                                    quantity: 1,
                                    date_id: event.is_multi_day
                                      ? (Array.isArray(tier.valid_date_ids) && tier.valid_date_ids.length === 1
                                        ? tier.valid_date_ids[0]
                                        : selectedDateId)
                                      : null,
                                    date_label: event.is_multi_day
                                      ? (event.event_dates || []).find((d) => d.id === (Array.isArray(tier.valid_date_ids) && tier.valid_date_ids.length === 1 ? tier.valid_date_ids[0] : selectedDateId))?.label
                                      : null,
                                  }, {
                                    onEventSwitch: () => toast('Cleared cart from previous event'),
                                  });
                                  toast.success(`Added · ${tier.name}`);
                                }}
                                className="font-mono text-[11px] text-[#B5FF00] hover:underline tracking-wider flex items-center gap-1"
                                data-testid={`add-to-cart-${tier.id}`}
                              >
                                + ADD TO CART
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {selectedTier && selectedTier.quantity_available > 0 && (
                    <div className="mb-6">
                      <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                        QUANTITY
                      </label>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="w-10 h-10 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:border-zinc-700 hover:text-white transition-colors"
                          data-testid="quantity-decrease"
                        >
                          <Minus size={16} strokeWidth={1.5} />
                        </button>
                        <span className="font-mono text-xl text-white w-8 text-center" data-testid="quantity-value">
                          {quantity}
                        </span>
                        <button
                          onClick={() => setQuantity(Math.min(selectedTier.quantity_available, quantity + 1))}
                          className="w-10 h-10 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:border-zinc-700 hover:text-white transition-colors"
                          data-testid="quantity-increase"
                        >
                          <Plus size={16} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-zinc-800 pt-6">
                    {token ? (
                      <div className="mb-4">
                        <FeeBreakdown quote={quote} loading={quoteLoading} />
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-mono text-sm text-zinc-400">FACE VALUE</span>
                          <span className="font-['Josefin_Sans'] text-2xl text-white" data-testid="total-price">
                            ₹{totalPrice.toFixed(0)}
                          </span>
                        </div>
                        <p className="text-zinc-500 text-xs font-mono mb-4">
                          Ticket price is GST-inclusive. Platform fee + 18% GST on it added at checkout. You can buy as a guest — no sign-up required.
                        </p>
                      </>
                    )}

                    {/* Cutoff line + BUY/SALES CLOSED state */}
                    {(event.sales_close_time || event.guestlist_close_time) && (
                      <p
                        className="text-zinc-500 text-xs font-mono mb-3"
                        data-testid="event-cutoff-line"
                      >
                        {event.sales_close_time && (
                          <>TICKET SALES CLOSE AT {event.sales_close_time} IST</>
                        )}
                        {event.guestlist_close_time && !event.sales_close_time && (
                          <>GUEST LIST CLOSES AT {event.guestlist_close_time} IST</>
                        )}
                      </p>
                    )}
                    <button
                      onClick={handleBuyTickets}
                      disabled={!selectedTier || selectedTier.quantity_available === 0 || event.sales_closed || salesPastCutoff || (event.is_multi_day && !selectedDateId && (() => {
                        const ids = Array.isArray(selectedTier?.valid_date_ids) ? selectedTier.valid_date_ids : null;
                        // Single-day tier → date required. Pass → no date required (covers all).
                        return !ids || ids.length <= 1;
                      })())}
                      className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed animate-pulse-glow"
                      data-testid="buy-tickets-btn"
                    >
                      {event.sales_closed || salesPastCutoff
                        ? 'SALES CLOSED'
                        : selectedTier?.quantity_available === 0
                          ? 'SOLD OUT'
                          : (event.is_multi_day && !selectedDateId && selectedTier && (!Array.isArray(selectedTier.valid_date_ids) || selectedTier.valid_date_ids.length <= 1))
                            ? 'SELECT A DATE'
                            : (selectedTier && Number(selectedTier.price) <= 0)
                              ? 'CLAIM FREE TICKET'
                              : 'BUY TICKETS'}
                    </button>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={(open) => { 
        if (!processingCheckout) {
          setShowCheckout(open);
          if (!open) setPaymentSuccess(false);
        }
      }}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Josefin_Sans'] text-2xl">
              {paymentSuccess ? 'PAYMENT SUCCESSFUL' : 'CONFIRM PURCHASE'}
            </DialogTitle>
          </DialogHeader>
          
          {paymentSuccess ? (
            <div className="text-center py-6">
              <div className="w-20 h-20 rounded-full bg-[#B5FF00]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={48} className="text-[#B5FF00]" />
              </div>
              <p className="text-zinc-400 mb-6">
                Your tickets have been confirmed! Check your email for details.
              </p>
              <button
                onClick={() => navigate('/my-tickets')}
                className="btn-primary"
                data-testid="view-tickets-btn"
              >
                VIEW MY TICKETS
              </button>
              {!user && (guestBuyer.email || user?.email) && (
                <SignupNudgeCard
                  email={guestBuyer.email || user?.email}
                  name={guestBuyer.name || user?.name}
                  context="ticket"
                />
              )}
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="bg-[#050505] border border-zinc-800 p-4">
                <h3 className="font-['Josefin_Sans'] text-lg text-white mb-1">{event.title}</h3>
                <p className="text-zinc-400 text-sm font-mono">{formatDate(event.date)}</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Ticket Type</span>
                  <span className="text-white">{selectedTier?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Quantity</span>
                  <span className="text-white font-mono">{quantity}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Price per ticket</span>
                  <span className="text-white font-mono">₹{selectedTier?.price.toFixed(0)}</span>
                </div>
              </div>

              <FeeBreakdown quote={quote} loading={quoteLoading} />

              {/* Promo code — only shown when a buyer-redeemable promo could
                  apply. Pre-applied discounts (handled server-side) just
                  appear in the fee breakdown. */}
              <div className="bg-[#050505] border border-zinc-800 p-3" data-testid="promo-code-section">
                {appliedPromo ? (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-mono text-[11px] text-zinc-500 tracking-wider">PROMO APPLIED</p>
                      <p className="font-mono text-sm text-[#B5FF00]">{appliedPromo}</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearPromo}
                      className="font-mono text-[11px] text-zinc-400 hover:text-red-400 tracking-wider"
                      data-testid="clear-promo-btn"
                    >
                      REMOVE
                    </button>
                  </div>
                ) : (
                  <div>
                    <label className="font-mono text-[11px] text-zinc-500 tracking-wider block mb-2">HAVE A PROMO CODE?</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoInput}
                        onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                        placeholder="E.G. EARLY20"
                        className="flex-1 h-10 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm font-mono tracking-wider focus:border-[#B5FF00] focus:outline-none"
                        data-testid="promo-code-input"
                      />
                      <button
                        type="button"
                        onClick={handleApplyPromo}
                        className="btn-secondary px-4 h-10 text-xs"
                        data-testid="apply-promo-btn"
                      >
                        APPLY
                      </button>
                    </div>
                    {promoError && (
                      <p className="text-red-400 text-[11px] font-mono mt-1" data-testid="promo-error">{promoError}</p>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={handleRazorpayPayment}
                disabled={processingCheckout}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="confirm-checkout-btn"
              >
                {processingCheckout ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    PROCESSING...
                  </>
                ) : (
                  'PAY WITH RAZORPAY'
                )}
              </button>

              <p className="text-zinc-600 text-xs text-center font-mono">
                Secure payment powered by Razorpay
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Guest-Checkout Gate — collects name/email/phone when the buyer isn't
          signed in. They can also choose to sign in instead. */}
      <Dialog open={showGuestGate} onOpenChange={setShowGuestGate}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white w-[calc(100vw-2rem)] max-w-md max-h-[90dvh] overflow-y-auto p-5 sm:p-6" data-testid="guest-checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Josefin_Sans'] text-2xl">CHECKOUT</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const name = guestBuyer.name.trim();
              const email = guestBuyer.email.trim();
              const phone = guestBuyer.phone.trim();
              if (!name || !email || !phone) {
                toast.error('Please fill in your name, email and phone');
                return;
              }
              // Strict email validation to catch typos that bounce (.con, gmial, missing dot, etc.)
              const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
              const BAD_TLD = new Set(['con', 'cim', 'cm', 'vom', 'xom', 'cin']);
              const BAD_PROVIDER = new Set(['gnail', 'gmial', 'yaho', 'yhoo', 'hotmial', 'outllok']);
              if (!EMAIL_RE.test(email)) {
                toast.error('Enter a valid email (e.g. name@example.com)');
                return;
              }
              const dom = email.split('@')[1] || '';
              const tld = dom.split('.').pop().toLowerCase();
              const provider = (dom.split('.')[0] || '').toLowerCase();
              if (BAD_TLD.has(tld)) {
                toast.error(`Did you mean .com instead of .${tld}? Please re-check your email.`);
                return;
              }
              if (BAD_PROVIDER.has(provider)) {
                toast.error("Looks like a typo in your email provider (e.g. gmial → gmail). Please re-check.");
                return;
              }
              if (!guestBuyer.agreed_terms) {
                toast.error('Please accept the Terms & Conditions to continue');
                return;
              }
              setShowGuestGate(false);
              setShowCheckout(true);
            }}
            noValidate
            className="space-y-4 mt-2"
          >
            <p className="text-zinc-400 text-sm">
              {user
                ? "Confirm your contact details below — your ticket will be sent to your email + phone."
                : "Buying as a guest? Fill in your contact details below — you'll receive your ticket by email."}
            </p>
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                FULL NAME *
              </label>
              <input
                type="text"
                value={guestBuyer.name}
                onChange={(e) => setGuestBuyer({ ...guestBuyer, name: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                data-testid="guest-buyer-name-input"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                EMAIL *
              </label>
              <input
                type="email"
                value={guestBuyer.email}
                onChange={(e) => setGuestBuyer({ ...guestBuyer, email: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                data-testid="guest-buyer-email-input"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                PHONE (WhatsApp) *
              </label>
              <input
                type="tel"
                value={guestBuyer.phone}
                onChange={(e) => setGuestBuyer({ ...guestBuyer, phone: e.target.value })}
                placeholder="+91 99999 99999"
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                data-testid="guest-buyer-phone-input"
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer pt-1" data-testid="guest-terms-label">
              <input
                type="checkbox"
                checked={guestBuyer.agreed_terms}
                onChange={(e) => setGuestBuyer({ ...guestBuyer, agreed_terms: e.target.checked })}
                className="mt-1 w-5 h-5 flex-shrink-0 accent-[#B5FF00]"
                data-testid="guest-terms-checkbox"
              />
              <span className="text-sm text-zinc-300 leading-relaxed">
                I accept the event&apos;s Terms &amp; Conditions and confirm I meet the age requirement.
              </span>
            </label>
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2 sticky bottom-0 bg-[#0A0A0A] pb-1">
              {!user && (
                <button
                  type="button"
                  onClick={() => { setShowGuestGate(false); navigate('/login', { state: { from: `/events/${id}` } }); }}
                  className="btn-secondary flex-1"
                  data-testid="guest-checkout-signin-btn"
                >
                  SIGN IN INSTEAD
                </button>
              )}
              <button type="submit" className="btn-primary flex-1" data-testid="guest-checkout-continue-btn">
                CONTINUE
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <CartBar
        eventId={event?.id}
        salesClosed={event?.sales_closed || salesPastCutoff}
        onCheckout={() => {
          // Reuse the same guest-gate dialog the single-tier flow uses.
          // The create-order handler detects cart.items > 0 and routes to
          // /checkout/create-cart-order.
          if (!token && cart.totals.allFree) {
            toast.error('Please sign in to claim free tickets');
            navigate(`/login?redirect=/events/${id}`);
            return;
          }
          setShowGuestGate(true);
        }}
      />
    </div>
  );
};

export default EventDetailPage;
